import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import CountUp from 'react-countup'
import { 
  Calendar, 
  Globe, 
  Rocket, 
  Award, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Clock, 
  Shield,
  Sparkles,
  ChevronRight,
  Building,
  Zap,
  Target,
  BarChart
} from 'lucide-react'
import { statsData, legacyData, partnerLogos } from '../../data/statsData'

const Slide6Legacy = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  })

  const [countersStarted, setCountersStarted] = useState(false)
  const [currentYear] = useState(new Date().getFullYear())
  const yearsActive = currentYear - legacyData.established
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (inView && !countersStarted) {
      setCountersStarted(true)
    }
  }, [inView, countersStarted])

  const milestones = [
    { year: 2018, event: 'Oman Enterprises Founded', icon: Building },
    { year: 2020, event: 'First Tech Project', icon: Rocket },
    { year: 2022, event: 'Global Expansion', icon: Globe },
    { year: 2023, event: 'AI Division Launch', icon: Zap },
    { year: 2024, event: 'Quavrion Rebrand', icon: Award },
    { year: currentYear, event: 'Current Operations', icon: TrendingUp }
  ]

  return (
    <div 
      id="legacy" 
      ref={ref}
      className="relative min-h-screen w-full bg-black overflow-hidden py-8 md:py-12"
    >
      {/* Background - Simplified */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/20 to-black"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        {/* Header - Mobile Optimized */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
            <span className="text-cyan-300 text-xs md:text-sm font-medium">OUR LEGACY</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            <span className="block text-white mb-1">Years of</span>
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Excellence
            </span>
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Building trust through proven results and continuous innovation
          </p>
        </div>

        {/* Origin Story - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-8 md:mb-12"
        >
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border border-gray-800 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              {/* Established Badge */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border-2 border-cyan-400 flex flex-col items-center justify-center">
                  <Calendar className="w-6 h-6 md:w-8 md:h-8 text-cyan-400 mb-1" />
                  <div className="text-xl md:text-2xl font-bold text-white">
                    {legacyData.established}
                  </div>
                  <div className="text-xs text-cyan-400 uppercase tracking-wider">
                    EST.
                  </div>
                </div>
              </div>

              {/* Story Content */}
              <div className="text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Built on {legacyData.company}
                </h3>
                <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-4">
                  {legacyData.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 text-sm text-gray-400 justify-center md:justify-start">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    <span>{yearsActive}+ Years Experience</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    <span>Proven Track Record</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid - Mobile Optimized */}
        <div className="mb-8 md:mb-12">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {statsData.map((stat, index) => {
              const Icon = stat.icon || TrendingUp
              return (
                <motion.div
                  key={stat.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-gray-800 p-4 text-center"
                >
                  <div className="flex justify-center mb-2">
                    <Icon className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                    {countersStarted ? (
                      <CountUp
                        start={0}
                        end={stat.value}
                        duration={2.5}
                        suffix={stat.suffix || ''}
                        prefix={stat.prefix || ''}
                      />
                    ) : (
                      '0' + (stat.suffix || '')
                    )}
                  </div>
                  <div className="text-sm md:text-base font-medium text-gray-300 mb-2">
                    {stat.label}
                  </div>
                  <div className="text-xs text-gray-500">
                    {stat.description}
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Timeline - Mobile Optimized */}
        <div className="mb-8 md:mb-12">
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              Our Journey
            </h3>
            <p className="text-sm md:text-base text-gray-400">
              Milestones that shaped our growth
            </p>
          </div>

          {/* Simplified Timeline for Mobile */}
          <div className="relative">
            {/* Timeline Line - Hide on mobile */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500 to-blue-500 transform -translate-x-1/2 hidden md:block"></div>
            
            {/* Milestones */}
            <div className="space-y-4 md:space-y-6">
              {milestones.map((milestone, index) => {
                const Icon = milestone.icon
                return (
                  <motion.div
                    key={milestone.year}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Desktop layout */}
                    <div className="hidden md:flex items-center">
                      {index % 2 === 0 ? (
                        <div className="w-2/5 pr-6 text-right">
                          <div className="inline-block">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 text-right">
                                <div className="text-lg font-bold text-white mb-1">
                                  {milestone.event}
                                </div>
                                <div className="text-cyan-400 font-bold">
                                  {milestone.year}
                                </div>
                              </div>
                              <div className="w-10 h-10 rounded-full bg-cyan-400/10 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-cyan-400" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-2/5"></div>
                      )}

                      {/* Center Dot */}
                      <div className="w-1/5 flex justify-center relative">
                        <div className="w-3 h-3 rounded-full bg-cyan-400 border-2 border-gray-900"></div>
                      </div>

                      {index % 2 === 1 ? (
                        <div className="w-2/5 pl-6">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-cyan-400/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div className="flex-1">
                              <div className="text-lg font-bold text-white mb-1">
                                {milestone.event}
                              </div>
                              <div className="text-cyan-400 font-bold">
                                {milestone.year}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="w-2/5"></div>
                      )}
                    </div>

                    {/* Mobile layout */}
                    <div className="md:hidden">
                      <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-gray-800 p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-cyan-400/10 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-cyan-400" />
                          </div>
                          <div className="flex-1">
                            <div className="text-base font-bold text-white mb-1">
                              {milestone.event}
                            </div>
                            <div className="text-cyan-400 font-bold">
                              {milestone.year}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Partners - Mobile Optimized */}
        <div className="mb-8 md:mb-12">
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
              Trusted Partners
            </h3>
            <p className="text-sm md:text-base text-gray-400">
              Working with industry leaders worldwide
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {partnerLogos.slice(0, isMobile ? 4 : partnerLogos.length).map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-gray-800 p-4 text-center"
              >
                <div className="flex flex-col items-center justify-center">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-white/10 to-transparent flex items-center justify-center mb-2">
                    <div className="text-lg md:text-xl font-bold text-cyan-400">
                      {partner.name.charAt(0)}
                    </div>
                  </div>
                  <div className="text-sm md:text-base font-medium text-white truncate w-full">
                    {partner.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 truncate w-full">
                    {partner.category}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trust Indicators - Mobile Optimized */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 md:mb-12">
          {[
            { 
              icon: Shield, 
              label: 'Enterprise Security', 
              value: 'Military Grade',
              description: 'Advanced protection protocols'
            },
            { 
              icon: Clock, 
              label: 'Support Response', 
              value: '< 15 mins',
              description: 'Average response time'
            },
            { 
              icon: CheckCircle, 
              label: 'Client Retention', 
              value: '95%',
              description: 'Annual retention rate'
            }
          ].map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl border border-gray-800 p-4 md:p-5 text-center"
              >
                <div className="flex justify-center mb-2 md:mb-3">
                  <Icon className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
                </div>
                <div className="text-xl md:text-2xl font-bold text-white mb-1">
                  {item.value}
                </div>
                <div className="text-sm md:text-base font-medium text-gray-300 mb-1">
                  {item.label}
                </div>
                <div className="text-xs text-gray-500">
                  {item.description}
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border border-cyan-400/30 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Ready to Build Your Legacy?
                </h3>
                <p className="text-sm md:text-base text-gray-300">
                  Join companies that trust our proven track record
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2">
                  <span>Start Partnership</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button className="px-6 py-3 bg-white/5 border border-gray-700 text-gray-300 rounded-lg font-medium hover:bg-white/10 transition-colors">
                  View Case Studies
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Year Badge - Mobile Optimized */}
      {/* {!isMobile && (
        <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-10">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-full border-2 border-cyan-400 flex flex-col items-center justify-center bg-gray-900/80 backdrop-blur-sm">
              <div className="text-xs text-cyan-400">EST.</div>
              <div className="text-xl font-bold text-white">{legacyData.established}</div>
            </div>
            <div className="mt-2 text-center bg-gray-900/80 backdrop-blur-sm px-3 py-1 rounded-lg">
              <div className="text-lg font-bold text-cyan-400">{yearsActive}</div>
              <div className="text-xs text-gray-400">Years</div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  )
}

export default Slide6Legacy